import { Alert, Box, Paper, Stack, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import SportsScoreIcon from '@mui/icons-material/SportsScore'
import { useTranslation } from 'react-i18next'

import { TestCreateSection } from '../TestCreateSection'
import type { TestCompletionMode } from '../testSettingsTypes'

const MODE_IDS: TestCompletionMode[] = ['classic', 'timed', 'series']

/** Тот же вид, что был у подсказок режима в форме теста из урока. */
function hintAlertSx(theme: Theme) {
  return {
    py: 0.5,
    px: 1,
    alignItems: 'flex-start' as const,
    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.09 : 0.12),
    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.28 : 0.4),
    '& .MuiAlert-icon': {
      fontSize: '1.1rem',
      mr: 0.75,
      py: 0,
      alignSelf: 'flex-start',
      opacity: 0.9,
    },
    '& .MuiAlert-message': {
      fontSize: '0.75rem',
      lineHeight: 1.45,
      py: 0,
    },
  }
}

/**
 * Режим завершения теста и количество слов.
 *
 * Подпись к количеству у группы другая: там потолок считается на ученика из
 * общего бюджета в 200 карточек, и молча показывать «не более 100» значило бы
 * врать учителю про то, сколько он получит.
 */
export function TestCompletionModeSection({
  mode,
  onModeChange,
  countNeed,
  onCountNeedChange,
  countNeedMax,
  groupParticipantsCount,
  durationMin,
  onDurationMinChange,
  seriesTarget,
  onSeriesTargetChange,
  countNeedHelpKey = 'tests.createForm.countNeedHelp',
}: {
  mode: TestCompletionMode
  onModeChange: (mode: TestCompletionMode) => void
  countNeed: number | ''
  onCountNeedChange: (value: number | '') => void
  /** Потолок ввода: у группы он меньше — бюджет карточек делится на участников. */
  countNeedMax: number
  /** Больше нуля — считаем набор групповым и объясняем потолок в подписи. */
  groupParticipantsCount?: number
  durationMin: number | ''
  onDurationMinChange: (value: number | '') => void
  seriesTarget: number | ''
  onSeriesTargetChange: (value: number | '') => void
  /** Подпись к количеству слов: у ученика она про его собственные настройки. */
  countNeedHelpKey?: string
}) {
  const { t } = useTranslation()
  const isGroupHint = (groupParticipantsCount ?? 0) > 2

  return (
    <TestCreateSection
      id="mode"
      icon={<SportsScoreIcon fontSize="small" />}
      title={t('tests.createForm.sectionCompletionMode')}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 1,
        }}
      >
        {MODE_IDS.map((id) => {
          const selected = mode === id
          return (
            <Paper
              key={id}
              variant="outlined"
              sx={(theme) => ({
                p: 1.25,
                borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
                bgcolor: selected
                  ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.22)
                  : theme.palette.background.paper,
                cursor: 'pointer',
                transition: theme.transitions.create(['background-color', 'border-color'], {
                  duration: theme.transitions.duration.shorter,
                }),
              })}
              onClick={() => onModeChange(id)}
            >
              <Typography
                variant="body2"
                sx={(theme) => ({
                  fontWeight: 600,
                  color: selected ? theme.palette.primary.main : theme.palette.text.primary,
                })}
              >
                {t(`tests.createForm.completionModes.${id}.title`)}
              </Typography>
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: selected
                    ? theme.palette.mode === 'light'
                      ? theme.palette.primary.dark
                      : theme.palette.primary.light
                    : theme.palette.text.secondary,
                })}
              >
                {t(`tests.createForm.completionModes.${id}.desc`)}
              </Typography>
            </Paper>
          )
        })}
      </Box>
      <TextField
        size="small"
        type="number"
        label={t('tests.createForm.countNeedLabel')}
        value={countNeed}
        onChange={(e) => {
          const v = e.target.value
          if (v === '') {
            onCountNeedChange('')
            return
          }
          const num = Number(v)
          if (!Number.isFinite(num)) return
          onCountNeedChange(Math.min(countNeedMax, Math.max(1, Math.trunc(num))))
        }}
        helperText={
          isGroupHint
            ? // Переменная не `count`: i18next считает его признаком множественного
              // числа и полез бы за формами `_one`/`_few`, которых у ключа нет.
              t('tests.createForm.countNeedHelpGroup', {
                max: countNeedMax,
                participants: groupParticipantsCount,
              })
            : t(countNeedHelpKey)
        }
        fullWidth
        inputProps={{ min: 1, max: countNeedMax }}
      />
      {mode === 'timed' ? (
        <Stack spacing={1}>
          <TextField
            size="small"
            type="number"
            label={t('tests.createForm.durationMinLabel')}
            value={durationMin}
            onChange={(e) => {
              const v = e.target.value
              onDurationMinChange(v === '' ? '' : Number(v))
            }}
            fullWidth
            required
            error={durationMin === ''}
            helperText={durationMin === '' ? t('tests.createForm.durationMinRequired') : undefined}
            inputProps={{ min: 1, max: 180 }}
          />
          <Alert severity="info" variant="outlined" sx={hintAlertSx}>
            {t('tests.createForm.durationMinHint')}
          </Alert>
        </Stack>
      ) : null}
      {mode === 'series' ? (
        <Stack spacing={1}>
          <TextField
            size="small"
            type="number"
            label={t('tests.createForm.seriesTargetLabel')}
            value={seriesTarget}
            onChange={(e) => {
              const v = e.target.value
              onSeriesTargetChange(v === '' ? '' : Number(v))
            }}
            fullWidth
            required
            error={seriesTarget === ''}
            helperText={seriesTarget === '' ? t('tests.createForm.seriesTargetRequired') : undefined}
            inputProps={{ min: 1, max: 50 }}
          />
          <Alert severity="info" variant="outlined" sx={hintAlertSx}>
            {t('tests.createForm.seriesTargetHint')}
          </Alert>
        </Stack>
      ) : null}
    </TestCreateSection>
  )
}
